import { QUADRANT_ROWS_SHOWN } from '@alison/api/performanceReport';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { ReportCard, SectionLabel } from '../ReportCard';
import { CreativeCell } from './CreativeCell';
import type { MatrixEntry, PerformanceReport, QuadrantName } from './data/api';
import { compactCurrency, count, formatKpi, percent, spendRatio } from './data/format';

const QUADRANT_ORDER: QuadrantName[] = ['scale', 'hiddenGems', 'fix', 'pause'];

const QUADRANT_COPY: Record<QuadrantName, { title: string; summary: string }> = {
  scale: { title: 'Scale', summary: 'High spend, beating the account. Put more behind these.' },
  hiddenGems: {
    title: 'Hidden Gems',
    summary: 'Beating the account on little spend. Worth testing wider.',
  },
  fix: { title: 'Fix', summary: 'High spend, trailing the account. The costliest problem.' },
  pause: { title: 'Pause', summary: 'Low spend and trailing. Retire or rework.' },
};

function useQuadrantTone(): Record<QuadrantName, string> {
  const theme = useTheme();
  return {
    scale: theme.palette.success.main,
    hiddenGems: theme.palette.secondary.main,
    fix: theme.palette.warning.main,
    pause: theme.palette.error.main,
  };
}

interface QuadrantButtonProps {
  name: QuadrantName;
  tone: string;
  entries: MatrixEntry[];
  selected: boolean;
  onSelect: () => void;
}

function QuadrantButton({
  name,
  tone,
  entries,
  selected,
  onSelect,
}: QuadrantButtonProps): ReactNode {
  const copy = QUADRANT_COPY[name];
  return (
    <ButtonBase
      onClick={onSelect}
      data-test={`quadrant-${name}`}
      aria-pressed={selected}
      sx={{
        textAlign: 'left',
        alignItems: 'stretch',
        flexDirection: 'column',
        gap: 1,
        p: 3,
        borderRadius: 1,
        borderLeft: `4px solid ${tone}`,
        bgcolor: 'background.paper',
        boxShadow: (theme) =>
          selected ? `inset 0 0 0 2px ${tone}` : `inset 0 0 0 1px ${theme.palette.divider}`,
        '&:hover': { bgcolor: 'background.default' },
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: tone }}>{copy.title}</Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.secondary' }}>
          {count(entries.length)}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: 'text.primary' }}>{copy.summary}</Typography>
    </ButtonBase>
  );
}

export function OpportunityMatrix({
  report,
  onOpenCreative,
}: {
  report: PerformanceReport;
  onOpenCreative: (creativeId: string) => void;
}): ReactNode {
  const tones = useQuadrantTone();
  const [selected, setSelected] = useState<QuadrantName>('scale');
  const { matrix, kpi, currencySymbol, performers } = report;
  const entries = matrix.quadrants[selected];
  const tags = matrix.topTags[selected];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 3,
        }}
      >
        {QUADRANT_ORDER.map((name) => (
          <QuadrantButton
            key={name}
            name={name}
            tone={tones[name]}
            entries={matrix.quadrants[name]}
            selected={selected === name}
            onSelect={() => setSelected(name)}
          />
        ))}
      </Box>

      <ReportCard>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 3,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: tones[selected] }}>
            {QUADRANT_COPY[selected].title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {count(matrix.plotted)} of {count(matrix.total)} clear the plotting floor ·{' '}
            {count(matrix.onPar)} performing at the account average are counted in neither band
          </Typography>
        </Box>

        {tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {tags.map((tag) => (
              <Chip
                key={`${tag.feature}-${tag.tag}`}
                size="small"
                label={`${tag.tag} (${tag.feature}, ${percent(tag.shareOfQuadrant, 0)})`}
              />
            ))}
          </Box>
        )}

        {entries.length === 0 ? (
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            No creative landed in this quadrant.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Creative</TableCell>
                <TableCell align="right">Spend</TableCell>
                <TableCell align="right">Spend ratio</TableCell>
                <TableCell align="right">{kpi.label}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.slice(0, QUADRANT_ROWS_SHOWN).map((entry) => (
                <TableRow key={entry.assetId}>
                  <CreativeCell row={entry} onOpen={onOpenCreative} />
                  <TableCell align="right">
                    {compactCurrency(entry.spend, currencySymbol)}
                  </TableCell>
                  <TableCell align="right">
                    {spendRatio(entry.spend, report.periods.current.spend)}
                  </TableCell>
                  <TableCell align="right">
                    {formatKpi(entry.kpiValue, kpi, currencySymbol)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <SectionLabel>
          Account {kpi.label} {formatKpi(performers.accountKpi, kpi, currencySymbol)} · top-spender
          floor {compactCurrency(matrix.spendFloor, currencySymbol)}
        </SectionLabel>
      </ReportCard>
    </Box>
  );
}
